import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  App as AntApp,
} from "antd";
import { DollarOutlined, MoreOutlined, SearchOutlined } from "@ant-design/icons";

import { catalogApi } from "../api/catalog";
import { paymentsApi } from "../api/payments";
import type { Shop } from "../api/types";
import { PageHeader } from "../components/PageHeader";
import { formatINR } from "../utils/format";

export function Collection() {
  const { notification } = AntApp.useApp();
  const [rows, setRows] = useState<Shop[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [collectingShop, setCollectingShop] = useState<Shop | null>(null);
  const [collectForm] = Form.useForm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await catalogApi.listShops({ search, page, page_size: pageSize });
      setRows(res.results);
      setTotal(res.count);
    } finally {
      setLoading(false);
    }
  }, [search, page, pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const onCollect = async () => {
    if (!collectingShop) return;
    try {
      const vals = await collectForm.validateFields();
      const res = await paymentsApi.create({
        shop: collectingShop.id,
        amount: vals.amount,
        method: vals.method,
        reference: vals.reference || "",
        notes: vals.notes || "",
      });
      notification.success({
        message: "Collection recorded",
        description: `New outstanding for ${collectingShop.name}: ${formatINR(res.outstanding_after)}`,
      });
      setCollectingShop(null);
      collectForm.resetFields();
      load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: Record<string, unknown> }; errorFields?: unknown };
      if (err.errorFields) return;
      const data = err.response?.data;
      const msg =
        (data && typeof data.detail === "string" ? data.detail : null) || "Could not record collection";
      notification.error({ message: msg });
    }
  };

  return (
    <div className="page-shell">
      <PageHeader
        title="Collection"
        subtitle="Track repayments received from shops"
      />

      <div className="toolbar-row">
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search name, area, city…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ maxWidth: 360 }}
        />
        <div style={{ marginLeft: "auto", fontSize: 13, color: "var(--text-secondary)" }}>
          {total} total
        </div>
      </div>

      <Table
        rowKey="id"
        size="middle"
        loading={loading}
        dataSource={rows}
        scroll={{ x: 900 }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        columns={[
          {
            title: "Shop",
            dataIndex: "name",
            width: 200,
            render: (v: string, row: Shop) => {
              const sub = [row.area, row.city, row.phone].filter(Boolean).join(" · ");
              return (
                <div>
                  <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>{v}</div>
                  {sub && <div className="small-muted">{sub}</div>}
                  {row.district_id && (
                    <Tag style={{ marginTop: 4, fontSize: 11 }}>District {row.district_id}</Tag>
                  )}
                </div>
              );
            },
          },
          {
            title: "Outstanding",
            dataIndex: "outstanding_amount",
            width: 140,
            align: "right" as const,
            render: (v: string) => (
              <span className="tabular" style={{ fontWeight: Number(v) > 0 ? 600 : 400 }}>
                {formatINR(v)}
              </span>
            ),
          },
          {
            title: "Opening Balance",
            dataIndex: "opening_balance",
            width: 150,
            align: "right" as const,
            render: (v: string) => <span className="tabular">{formatINR(v)}</span>,
          },
          {
            title: "Employees",
            dataIndex: "allocated_employees",
            width: 160,
            render: (emps: Shop["allocated_employees"]) =>
              emps && emps.length > 0 ? (
                <Space size={[4, 4]} wrap>
                  {emps.slice(0, 2).map((e) => (
                    <Tag key={e.id} style={{ margin: 0 }}>{e.full_name}</Tag>
                  ))}
                  {emps.length > 2 && <Tag style={{ margin: 0 }}>+{emps.length - 2}</Tag>}
                </Space>
              ) : (
                <span className="small-muted">—</span>
              ),
          },
          {
            title: "",
            key: "_actions",
            width: 56,
            fixed: "right" as const,
            align: "center" as const,
            render: (_: unknown, row: Shop) => (
              <Dropdown
                trigger={["click"]}
                menu={{
                  items: [
                    {
                      key: "collect",
                      icon: <DollarOutlined />,
                      label: "Record Collection",
                      onClick: () => setCollectingShop(row),
                    },
                  ],
                }}
              >
                <Button type="text" icon={<MoreOutlined />} />
              </Dropdown>
            ),
          },
        ]}
      />

      <Modal
        title={collectingShop ? `Record Collection — ${collectingShop.name}` : "Record Collection"}
        open={!!collectingShop}
        onCancel={() => setCollectingShop(null)}
        onOk={onCollect}
        okText="Record Collection"
        destroyOnClose
      >
        {collectingShop && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: 8,
              marginBottom: 16,
              fontSize: 13,
            }}
          >
            <div>
              <div className="small-muted">Opening Balance</div>
              <div className="tabular">{formatINR(collectingShop.opening_balance)}</div>
            </div>
            <div>
              <div className="small-muted">Outstanding</div>
              <div className="tabular">{formatINR(collectingShop.outstanding_amount)}</div>
            </div>
          </div>
        )}
        <Form
          layout="vertical"
          form={collectForm}
          requiredMark={false}
          preserve={false}
          initialValues={{ method: "cash" }}
        >
          <Form.Item
            label="Amount Collected (₹)"
            name="amount"
            rules={[{ required: true, message: "Enter an amount" }]}
          >
            <InputNumber min={0.01} step={500} style={{ width: "100%" }} placeholder="e.g. 5000" />
          </Form.Item>
          <Form.Item label="Method" name="method">
            <Select
              options={[
                { label: "Cash", value: "cash" },
                { label: "UPI", value: "upi" },
                { label: "Cheque", value: "cheque" },
                { label: "Bank Transfer", value: "bank_transfer" },
                { label: "Other", value: "other" },
              ]}
            />
          </Form.Item>
          <Form.Item label="Reference" name="reference" extra="Optional — cheque no., UPI ref, etc.">
            <Input maxLength={128} />
          </Form.Item>
          <Form.Item label="Notes" name="notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
